/**
 * @file Defines the form inputs for creating an organization public key.
 */

import React from "react";

import InputExtra from "@components/Form/InputExtra";
import { TYPE_RADIO, TYPE_STRING, TYPE_TEXT } from "@components/Form";
import { type OrganizationPublicKeyAudience } from "@api/types";

import {
  LABEL_BACKSTAGE,
  LABEL_INVESTMENTS,
  AUDIENCE_BACKSTAGE,
  AUDIENCE_INVESTMENTS,
} from "./audiences";

const PUBLIC_KEY_ROWS = 8;

const LABEL_AUDIENCE = "API";

const EXTRA_NAME = "Give a name that tells you which of your clients uses this key.";
const EXTRA_AUDIENCE = "One key serves one API. To reach the two APIs, add two keys. You cannot change this value after you add the key.";
const EXTRA_ISSUER = "Give the iss claim of your client. Each ID token of this key must carry the same value.";
const EXTRA_PUBLIC_KEY = "Paste the public key of your client in PEM format. Keep the BEGIN PUBLIC KEY and the END PUBLIC KEY markers.";

const DESCRIPTION_INVESTMENTS = "Your client signs in your own investors. Each ID token gives the address of one investor account of your organization.";
const DESCRIPTION_BACKSTAGE = "Your client acts as the user that adds the key. Each ID token carries the address and the access of that user.";

const AUDIENCE_OPTIONS = [
  {
    label: LABEL_INVESTMENTS,
    value: AUDIENCE_INVESTMENTS,
    description: DESCRIPTION_INVESTMENTS,
  },
  {
    label: LABEL_BACKSTAGE,
    value: AUDIENCE_BACKSTAGE,
    description: DESCRIPTION_BACKSTAGE,
  },
];

type AudienceOption = {
  label: string;
  value: OrganizationPublicKeyAudience;
  description: string;
};

type CreateInput = {
  name: "name" | "audience" | "issuer" | "publicKey";
  type:
    | typeof TYPE_RADIO
    | typeof TYPE_STRING
    | typeof TYPE_TEXT;
  label?: string;
  options?: AudienceOption[];
  placeholder?: string;
  itemProps: {
    extra: JSX.Element;
  };
  inputProps?: {
    rows: number;
  };
};

/**
 * Builds the form inputs for the create-public-key form.
 */
const getCreateInputs = (): CreateInput[] => {
  return [
    {
      name: "name",
      type: TYPE_STRING,
      itemProps: {
        extra: <InputExtra text={EXTRA_NAME} />,
      },
    },
    {
      name: "audience",
      type: TYPE_RADIO,
      label: LABEL_AUDIENCE,
      options: AUDIENCE_OPTIONS,
      placeholder: "",
      itemProps: {
        extra: <InputExtra text={EXTRA_AUDIENCE} />,
      },
    },
    {
      name: "issuer",
      type: TYPE_STRING,
      itemProps: {
        extra: <InputExtra text={EXTRA_ISSUER} />,
      },
    },
    {
      name: "publicKey",
      type: TYPE_TEXT,
      itemProps: {
        extra: <InputExtra text={EXTRA_PUBLIC_KEY} />,
      },
      inputProps: {
        rows: PUBLIC_KEY_ROWS,
      },
    },
  ];
};

export default getCreateInputs;
